/**
 * Review Step
 *
 * Final step - review all setup data before creating accounts
 */

import { TEMPLATE_METADATA } from '../../constants/coa-templates'

import type { BankAccountInput, CompanySetupData, EquityAccountInput, TemplateMetadata } from '../../types/company-setup'

interface ReviewStepProps {
  setupData: CompanySetupData
  onSubmit: () => void
  onBack: () => void
  isSubmitting: boolean
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatTimezone(tz: string | undefined): JSX.Element | string {
  if (!tz) {return <span className="italic text-gray-500">Not set</span>}
  return tz.replace(/_/g, ' ')
}

// ── Sub-components ─────────────────────────────────────────────────────

function ReviewWarning(): JSX.Element {
  return (
    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path clipRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" fillRule="evenodd" />
        </svg>
        <div className="text-sm text-amber-700 dark:text-amber-300">
          <p className="font-medium mb-1">Review Carefully</p>
          <p>Please review all information before proceeding. Clicking &quot;Create Company&quot; will initialize your chart of accounts and create opening balance entries. This action cannot be easily undone.</p>
        </div>
      </div>
    </div>
  )
}

function CompanyInfoSection({ companyInfo }: { companyInfo: CompanySetupData['companyInfo'] }): JSX.Element {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="bg-surface-secondary px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-foreground">Company Information</h3>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex justify-between">
          <span className="text-foreground-secondary">Company Name:</span>
          <span className="font-medium text-foreground">{companyInfo.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground-secondary">Fiscal Year:</span>
          <span className="font-medium text-foreground">
            {new Date(companyInfo.fiscalYearStart).toLocaleDateString()} - {new Date(companyInfo.fiscalYearEnd).toLocaleDateString()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground-secondary">Currency:</span>
          <span className="font-medium text-foreground">{companyInfo.currency}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground-secondary">Timezone:</span>
          <span className="font-medium text-foreground">
            {formatTimezone(companyInfo.timezone)}
          </span>
        </div>
      </div>
    </div>
  )
}

function ChartOfAccountsSection({ template }: { template: TemplateMetadata }): JSX.Element {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="bg-surface-secondary px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-foreground">Chart of Accounts Template</h3>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex justify-between">
          <span className="text-foreground-secondary">Template:</span>
          <span className="font-medium text-foreground">{template.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground-secondary">Accounts to Create:</span>
          <span className="font-medium text-foreground">{template.accountCount} accounts</span>
        </div>
        <div className="flex justify-between items-start">
          <span className="text-foreground-secondary">Industries:</span>
          <span className="font-medium text-foreground text-right">
            {template.industries.join(', ')}
          </span>
        </div>
      </div>
    </div>
  )
}

function BankAccountsSection({ bankAccounts, currency, totalBankBalance }: {
  bankAccounts: BankAccountInput[]
  currency: string
  totalBankBalance: number
}): JSX.Element {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="bg-surface-secondary px-4 py-3 border-b border-border flex justify-between items-center">
        <h3 className="font-semibold text-foreground">Bank Accounts</h3>
        <span className="text-sm text-foreground-secondary">
          {bankAccounts.length} account{bankAccounts.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="p-4">
        {bankAccounts.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-500 italic">No bank accounts configured</p>
        ) : (
          <div className="space-y-3">
            {bankAccounts.map((account, index) => (
              <div key={index} className="p-3 bg-surface-secondary rounded-lg">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium text-foreground">{account.name}</span>
                  <span className="text-green-600 dark:text-green-400 font-semibold">
                    {account.currency} {(account.openingBalance ?? 0).toFixed(2)}
                  </span>
                </div>
                <div className="text-sm text-foreground-secondary">
                  {account.bankName && <span>{account.bankName} • </span>}
                  <span>Opening: {account.openingDate ? new Date(account.openingDate.includes('T') ? account.openingDate : `${account.openingDate}T00:00:00`).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Total Bank Balance:</span>
                <span className="font-bold text-green-600 dark:text-green-400">
                  {currency} {totalBankBalance.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function EquityAccountsSection({ equityAccounts, currency, totalEquityInvestment }: {
  equityAccounts: EquityAccountInput[]
  currency: string
  totalEquityInvestment: number
}): JSX.Element {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="bg-surface-secondary px-4 py-3 border-b border-border flex justify-between items-center">
        <h3 className="font-semibold text-foreground">Owner Equity</h3>
        <span className="text-sm text-foreground-secondary">
          {equityAccounts.length} owner{equityAccounts.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="p-4">
        {equityAccounts.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-500 italic">No equity accounts configured</p>
        ) : (
          <div className="space-y-3">
            {equityAccounts.map((equity, index) => (
              <div key={index} className="p-3 bg-surface-secondary rounded-lg">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <span className="font-medium text-foreground block">{equity.ownerName}</span>
                    <span className="text-sm text-foreground-secondary">{equity.accountName}</span>
                  </div>
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">
                    {currency} {(equity.investmentAmount ?? 0).toFixed(2)}
                  </span>
                </div>
                <div className="text-sm text-foreground-secondary">
                  Invested: {equity.investmentDate ? new Date(equity.investmentDate.includes('T') ? equity.investmentDate : `${equity.investmentDate}T00:00:00`).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Total Investment:</span>
                <span className="font-bold text-blue-600 dark:text-blue-400">
                  {currency} {totalEquityInvestment.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryStats({ currency, totalBankBalance, totalEquityInvestment }: {
  currency: string
  totalBankBalance: number
  totalEquityInvestment: number
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg border border-green-200 dark:border-green-800">
        <div className="text-sm text-green-700 dark:text-green-300 mb-1">Total Assets</div>
        <div className="text-2xl font-bold text-green-900 dark:text-green-100">
          {currency} {totalBankBalance.toFixed(2)}
        </div>
      </div>
      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="text-sm text-blue-700 dark:text-blue-300 mb-1">Total Equity</div>
        <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
          {currency} {totalEquityInvestment.toFixed(2)}
        </div>
      </div>
    </div>
  )
}

function CheckIcon(): JSX.Element {
  return (
    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fillRule="evenodd" />
    </svg>
  )
}

function WhatWillHappenSection({ template, bankAccountCount, equityAccountCount }: {
  template: TemplateMetadata
  bankAccountCount: number
  equityAccountCount: number
}): JSX.Element {
  return (
    <div className="p-4 bg-surface-secondary rounded-lg">
      <h4 className="font-semibold text-foreground mb-2">What will happen when you click &quot;Create Company&quot;:</h4>
      <ul className="space-y-2 text-sm text-foreground-secondary">
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span><strong>{template.accountCount} accounts</strong> will be created from the {template.name} template</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span><strong>{bankAccountCount} bank accounts</strong> will be created with their opening balances</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span><strong>{equityAccountCount} equity accounts</strong> will be created for owner capital</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span>Opening balance <strong>journal entries</strong> will be created automatically</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span>You&apos;ll be ready to start recording transactions immediately</span>
        </li>
      </ul>
    </div>
  )
}

function ActionButtons({ onSubmit, onBack, isSubmitting }: {
  onSubmit: () => void
  onBack: () => void
  isSubmitting: boolean
}): JSX.Element {
  return (
    <div className="flex justify-between pt-4">
      <button
        className="px-3 py-2 border border-border text-foreground-secondary rounded-lg hover:bg-surface-secondary transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isSubmitting}
        type="button"
        onClick={onBack}
      >
        Back
      </button>
      <button
        className="px-3 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        disabled={isSubmitting}
        type="button"
        onClick={onSubmit}
      >
        {isSubmitting ? (
          <>
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor" />
            </svg>
            Creating Company...
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
            Create Company
          </>
        )}
      </button>
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────

export function ReviewStep({
  setupData,
  onSubmit,
  onBack,
  isSubmitting,
}: ReviewStepProps): JSX.Element {
  const template = TEMPLATE_METADATA[setupData.selectedTemplate]
  const totalBankBalance = setupData.bankAccounts.reduce((sum, acc) => sum + (acc.openingBalance ?? 0), 0)
  const totalEquityInvestment = setupData.equityAccounts.reduce((sum, acc) => sum + (acc.investmentAmount ?? 0), 0)
  const { currency } = setupData.companyInfo

  return (
    <div className="space-y-6">
      <ReviewWarning />
      <CompanyInfoSection companyInfo={setupData.companyInfo} />
      <ChartOfAccountsSection template={template} />
      <BankAccountsSection bankAccounts={setupData.bankAccounts} currency={currency} totalBankBalance={totalBankBalance} />
      <EquityAccountsSection currency={currency} equityAccounts={setupData.equityAccounts} totalEquityInvestment={totalEquityInvestment} />
      <SummaryStats currency={currency} totalBankBalance={totalBankBalance} totalEquityInvestment={totalEquityInvestment} />
      <WhatWillHappenSection bankAccountCount={setupData.bankAccounts.length} equityAccountCount={setupData.equityAccounts.length} template={template} />
      <ActionButtons isSubmitting={isSubmitting} onBack={onBack} onSubmit={onSubmit} />
    </div>
  )
}
